import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BookOpen, Clock, ChevronRight, Search, ArrowRight } from "lucide-react";
import blogPosts, { blogCategories } from "@/data/blogs";

const API = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  Banking: "bg-vedanti-navy text-white",
  SSC: "bg-vedanti-orange text-white",
  Railway: "bg-green-600 text-white",
  MBA: "bg-vedanti-mango text-vedanti-navy",
  Insurance: "bg-blue-600 text-white",
  Career: "bg-purple-600 text-white",
  Vedanti: "bg-gradient-to-r from-vedanti-orange to-vedanti-mango text-white",
};

export default function ResourcesPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [posts, setPosts] = useState(blogPosts);

  useEffect(() => {
    fetch(`${API}/api/blogs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((dbBlogs) => {
        if (!Array.isArray(dbBlogs) || dbBlogs.length === 0) return;
        const normalized = dbBlogs.map((b) => ({
          id: `db-${b.id}`,
          slug: b.slug,
          title: b.title,
          category: b.category,
          date: b.date,
          readTime: b.read_time,
          excerpt: b.excerpt,
        }));
        setPosts([...normalized, ...blogPosts]);
      })
      .catch(() => {});
  }, []);

  const filtered = posts.filter((b) => {
    const matchCat = activeCategory === "All" || b.category === activeCategory;
    const matchSearch = !searchQuery || b.title.toLowerCase().includes(searchQuery.toLowerCase()) || b.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div data-testid="resources-page">
      <Helmet>
        <title>Exam Prep Resources & Blog | Vedanti Academy</title>
        <meta name="description" content="Free study resources, exam preparation guides, shortcuts, and tips for Banking, SSC, MBA CET, Railway & Insurance exams by Vedanti Academy experts." />
        <link rel="canonical" href="https://vedantiacademy.com/resources" />
      </Helmet>

      {/* Hero */}
      <section className="bg-vedanti-navy relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.12)_0%,_transparent_50%)]"></div>
        <div className="container-main py-14 md:py-20 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-vedanti-orange/15 text-vedanti-orange font-bold text-sm uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
            <BookOpen size={16} /> Free Resources
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white mb-4">Exam Preparation <span className="text-vedanti-mango">Resources</span></h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">Expert tips, shortcuts, strategies, and study guides to help you crack Banking, SSC, MBA, Railway & Insurance exams.</p>

          {/* Search */}
          <div className="mt-8 max-w-lg mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-vedanti-orange/50 focus:bg-white/15 transition-all"
              data-testid="resources-search"
            />
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="bg-white border-b border-slate-100 sticky top-16 md:top-20 z-30">
        <div className="container-main py-3 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-max">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${activeCategory === cat ? "bg-vedanti-navy text-white shadow-lg" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`cat-filter-${cat.toLowerCase()}`}
              >
                {cat} {cat !== "All" && <span className="text-xs opacity-70">({posts.filter(b => b.category === cat).length})</span>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <p className="text-slate-500 text-sm mb-6" data-testid="results-count">{filtered.length} article{filtered.length !== 1 ? "s" : ""} found</p>
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400 text-lg">No articles found. Try a different search or category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post) => (
                <Link
                  to={`/resources/${post.slug}`}
                  key={post.id}
                  className="group bg-white rounded-2xl border border-slate-100 hover:border-vedanti-orange/30 hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden"
                  data-testid={`blog-card-${post.id}`}
                >
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${categoryColors[post.category] || "bg-slate-200 text-slate-700"}`}>
                        {post.category}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
                    </div>
                    <h2 className="font-heading font-bold text-vedanti-navy text-base leading-snug mb-2 group-hover:text-vedanti-orange transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-slate-400">{post.date}</span>
                      <span className="text-vedanti-orange font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
